'use client';

import Button, { ButtonVariants } from '@pixum/button';
import { Icon } from '@pixum/icon';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { ToggleCard } from '@pixum/toggle-card';
import { Dialog, DialogContent, DialogFooter, DIALOG_ROLES } from '@pixum/web-dialog';
import { useEffect, useState } from 'react';
import type { CeweAddressComparison } from 'services';
import type { Device } from 'data-layer';
import { DEVICES } from 'data-layer/constants';
import {
  ADDRESS_VALIDATION_CHOICES,
  ADDRESS_VALIDATION_HEADING_ID,
  type AddressValidationChoice
} from '../../constants';
import styles from '../../CartAddressStep.module.css';
import { AddressCardContent } from './partials/AddressCardContent';

interface AddressValidationDialogProps {
  device: Device;
  open: boolean;
  comparison: CeweAddressComparison;
  headingLabel: string;
  subtitleLabel: string;
  suggestionCardLabel: string;
  originalCardLabel: string;
  acceptAddressLabel: string;
  closeAriaLabel: string;
  onAcceptSuggestion: () => void;
  onKeepOriginal: () => void;
  onClose: () => void;
}

/** Presents a CEWE correction and lets the customer choose which address to save. */
export function AddressValidationDialog({
  device,
  open,
  comparison,
  headingLabel,
  subtitleLabel,
  suggestionCardLabel,
  originalCardLabel,
  acceptAddressLabel,
  closeAriaLabel,
  onAcceptSuggestion,
  onKeepOriginal,
  onClose
}: AddressValidationDialogProps) {
  const [selectedAddress, setSelectedAddress] = useState<AddressValidationChoice>(ADDRESS_VALIDATION_CHOICES.SUGGESTED);

  useEffect(() => {
    if (open) {
      setSelectedAddress(ADDRESS_VALIDATION_CHOICES.SUGGESTED);
    }
  }, [open]);

  const isSuggestionSelected = selectedAddress === ADDRESS_VALIDATION_CHOICES.SUGGESTED;

  const submitSelectedAddress = () => {
    if (isSuggestionSelected) {
      onAcceptSuggestion();

      return;
    }

    onKeepOriginal();
  };

  return (
    <Dialog
      className={styles.cartAddressValidationDialog}
      fullScreen={device === DEVICES.MOBILE}
      header={
        <div className={styles.cartAddressValidationHeader}>
          <Button
            aria-label={closeAriaLabel}
            label={closeAriaLabel}
            type="button"
            variant="text"
            icon={<Icon name="close" ariaHidden={true} />}
            onClick={onClose}
          />
        </div>
      }
      content={
        <DialogContent>
          <div className={styles.cartAddressValidationContent} aria-labelledby={ADDRESS_VALIDATION_HEADING_ID}>
            <Text id={ADDRESS_VALIDATION_HEADING_ID} variant={TEXT_VARIANT.TITLE_2} tag={TEXT_TAG.H2}>
              {headingLabel}
            </Text>
            <Text variant={TEXT_VARIANT.SUBHEADLINE} tag={TEXT_TAG.P}>
              {subtitleLabel}
            </Text>
            <div role="radiogroup" aria-label={headingLabel} className={styles.cartAddressValidationCards}>
              <ToggleCard
                selected={isSuggestionSelected}
                onChange={() => setSelectedAddress(ADDRESS_VALIDATION_CHOICES.SUGGESTED)}
                label={suggestionCardLabel}
              >
                <AddressCardContent address={comparison.suggested} compareAddress={comparison.original} />
              </ToggleCard>
              <ToggleCard
                selected={!isSuggestionSelected}
                onChange={() => setSelectedAddress(ADDRESS_VALIDATION_CHOICES.ORIGINAL)}
                label={originalCardLabel}
              >
                <AddressCardContent address={comparison.original} />
              </ToggleCard>
            </div>
          </div>
        </DialogContent>
      }
      footer={
        <DialogFooter
          className={styles.cartAddressValidationFooter}
          role={DIALOG_ROLES.DEFAULT}
          right={
            <Button type="button" variant={ButtonVariants.PRIMARY} onClick={submitSelectedAddress}>
              {acceptAddressLabel}
            </Button>
          }
        />
      }
      onClose={onClose}
      open={open}
      role={DIALOG_ROLES.ALERT}
      showCloseButton={false}
    />
  );
}
